<script setup lang="ts">
import type { WithdrawAccountSelection } from './StepAccount.vue';

// Detalhes da solicitação (ENG-868): última tela antes do POST /new-withdraws.
// Não é etapa do stepper — é saída do fluxo, por isso vive em `screen`.
//
// Além do resumo do Figma, esta tela carrega dois avisos que o desenho não
// mostra e que vinham do fluxo antigo (components/withdraw/index.vue):
//   - validação completa de cadastro no primeiro saque acima do gatilho;
//   - aviso de que o saldo de bônus será perdido.

type Props = {
  amountCents: number;
  selection: WithdrawAccountSelection;
};

const { amountCents, selection } = defineProps<Props>();

type Emits = {
  confirm: [];
  cancel: [];
  'change-info': [];
};

const emit = defineEmits<Emits>();

const { $localePath, $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { showMoneyFormat } = useWallet();
const { bonusWallet, currentWithdrawMethod } = storeToRefs(useWalletStore());
const { allWithdrawMethods } = usePayments();
const { isVerifiedToWithdraw } = useUserValidations();
const { getMinValueToTriggerModule } = useModulesValidation();
const { isBusy, close } = useWithdrawModal();
const { userBankAccounts } = useUserData();

const isLoading = ref(false);

// Nome do gateway como o jogador o viu na Etapa 2 (o título vem do core); o
// slug é o fallback.
const methodLabel = computed(() => {
  const method = allWithdrawMethods.value.find(
    (m) => m.slug === currentWithdrawMethod.value,
  );

  return (method?.title || currentWithdrawMethod.value || '').toUpperCase();
});

const amountLabel = computed(() =>
  showMoneyFormat({ value: amountCents / 100 }),
);

const family = computed(() =>
  withdrawMethodFamily(currentWithdrawMethod.value),
);

// Conta bancária cadastrada escolhida na Etapa 1: o `selectedKeyValue` fica
// VAZIO nesse caminho (quem preenche é o formulário de chave, que nem aparece
// quando há contas salvas), então sem resolver o id contra a lista a tela de
// confirmação não mostrava destino nenhum no caminho padrão do Brasil.
const selectedBankAccount = computed(() => {
  if (!selection.selectedBankAccountId) return null;

  return (
    userBankAccounts.value?.find(
      (account) => account.id === selection.selectedBankAccountId,
    ) || null
  );
});

// Para onde o dinheiro vai, na linguagem da família: chave Pix, conta
// mexicana ou telefone. Sem isto, o jogador fora do Brasil confirmava o saque
// sem ver o destino.
const destination = computed<{ label: string; value: string } | null>(() => {
  if (family.value === 'bank-mx' && selection.selectedBankNumber) {
    return {
      label: 'account.user_data.mex.account_number',
      value: selection.selectedBankNumber,
    };
  }

  if (family.value === 'phone' && selection.selectedPhoneNumber) {
    return {
      label: 'account.phone',
      value: `${selection.selectedPhoneDdi || ''}${selection.selectedPhoneNumber}`,
    };
  }

  const account = selectedBankAccount.value;

  if (account?.key_pix) {
    return {
      label: 'account.wallet.withdraw_modal.detail_pix_key',
      value: account.key_pix,
    };
  }

  // Conta salva sem chave Pix (só dados bancários): o banco + a conta são o
  // destino reconhecível.
  if (account?.number) {
    return {
      label: 'account.user_data.account_number',
      value: [account.bank_name, account.number].filter(Boolean).join(' · '),
    };
  }

  if (selection.selectedKeyValue) {
    return {
      label: 'account.wallet.withdraw_modal.detail_pix_key',
      value: selection.selectedKeyValue,
    };
  }

  return null;
});

const userWalletHasBonus = computed(() => bonusWallet.value.credit > 0);

// Primeiro saque acima do gatilho e cadastro ainda não verificado: em vez de
// confirmar, a tela pede a validação — mesma regra do fluxo antigo.
const needsFullValidation = computed(
  () =>
    amountCents >= getMinValueToTriggerModule('first_withdraw') &&
    !isVerifiedToWithdraw.value,
);

async function onCompleteRegisterClick() {
  const { isValidationModuleEnabled } = useModulesValidation();

  if (!isValidationModuleEnabled('first_withdraw')) {
    // Fecha ANTES de navegar: sem isto o sheet full-screen (e o scroll lock do
    // body) ficava por cima da Minha Conta. O goToChangeCountry da Etapa 1 já
    // faz nesta ordem.
    close();

    await navigateTo({ path: $localePath('/user/account') });

    return;
  }

  const { fetchUserProfile } = useUserData();

  isLoading.value = true;

  try {
    await fetchUserProfile({ force: true });

    await $containment.fetchCurrentPending('first_withdraw');
  } finally {
    // O `finally` é o ponto: sem pendência o fluxo antigo deixava o loading
    // ligado pra sempre, cobrindo até o X do topbar.
    isLoading.value = false;
  }

  if ($containment.hasPendencyOn('first_withdraw')) {
    await $containment.handle('first_withdraw', () => {
      openToaster({
        message: t('account.wallet.withdraw_modal.validation_success'),
        type: 'success',
      });
    });
  }
}
</script>

<template>
  <div class="wdm-confirm">
    <BaseLoading
      class="!bg-auth-bg-primary z-20"
      :icon-loader="true"
      :show="isLoading || isBusy"
    />

    <template v-if="needsFullValidation">
      <img
        alt=""
        aria-hidden="true"
        class="wdm-validation-icon"
        :src="getAssetsUrlForCDN('/assets/images/svg/complete.svg')"
      />

      <p
        class="wdm-validation-text"
        v-html="
          $t('account.wallet.withdraw_modal.full_validation_alert', {
            amount: showMoneyFormat({
              value: getMinValueToTriggerModule('first_withdraw') / 100,
            }),
          })
        "
      />

      <UserAccountWithdrawAlert
        :on-top="true"
        :show-text="false"
        :without-bg="true"
      />

      <div class="wdm-actions">
        <button
          class="wdm-btn is-confirm"
          type="button"
          @click="onCompleteRegisterClick"
        >
          {{ $t('account.wallet.withdraw_modal.complete') }}
        </button>
        <button
          class="wdm-btn is-cancel"
          type="button"
          @click="emit('cancel')"
        >
          {{ $t('account.wallet.withdraw_modal.cancel') }}
        </button>
      </div>
    </template>

    <template v-else>
      <label class="wdm-label">{{
        $t('deposit.general.payment_method')
      }}</label>

      <p class="wdm-field">{{ methodLabel }}</p>

      <p class="wdm-line">
        {{ $t('account.wallet.withdraw_modal.detail_value') }}:
        <strong>{{ amountLabel }}</strong>
      </p>

      <p
        v-if="destination"
        class="wdm-line"
      >
        {{ $t(destination.label) }}:
        <strong>{{ destination.value }}</strong>
      </p>

      <button
        class="wdm-change"
        type="button"
        @click="emit('change-info')"
      >
        <span>{{ $t('account.wallet.withdraw_modal.change_info') }}</span>
        <NuxtIcon name="arrows/angle_right" />
      </button>

      <!-- Sacar o saldo real zera o bônus: o jogador é avisado de novo aqui,
           como no fluxo antigo. -->
      <div
        v-if="userWalletHasBonus"
        class="wdm-bonus-alert"
        v-html="$t('account.wallet.withdraw_modal.bonus_second_alert')"
      ></div>

      <p class="wdm-question">
        {{ $t('account.wallet.withdraw_modal.withdraw_alert') }}
      </p>

      <div class="wdm-actions">
        <button
          class="wdm-btn is-confirm"
          :disabled="isBusy"
          type="button"
          @click="emit('confirm')"
        >
          {{ $t('account.wallet.withdraw_modal.confirm') }}
        </button>
        <button
          class="wdm-btn is-cancel"
          :disabled="isBusy"
          type="button"
          @click="emit('cancel')"
        >
          {{ $t('account.wallet.withdraw_modal.cancel') }}
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
$field-bg: #131a2c;
$field-text: #e2e3e5;

.wdm-confirm {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.wdm-label {
  font-size: 12px;
  color: #dddee0;
}

.wdm-field {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: $field-bg;
  color: $field-text;
  font-size: 13px;
}

.wdm-line {
  font-size: 12px;
  color: #dddee0;

  strong {
    color: #fff;
    font-weight: 700;
  }
}

.wdm-change {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: $field-bg;
  color: $field-text;
  font-size: 12px;
  cursor: pointer;

  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.wdm-bonus-alert {
  padding: 10px 12px;
  border-radius: 8px;
  background: #29231f;
  color: #f5a524;
  font-size: 11px;
  line-height: 16px;
}

.wdm-question {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.wdm-validation-icon {
  width: 96px;
  height: 96px;
  margin: 8px auto 0;
}

.wdm-validation-text {
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: $field-text;
}

.wdm-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.wdm-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;

  &.is-confirm {
    background: #17c964;
  }

  &.is-cancel {
    background: #dc2626;
  }

  &[disabled]:not([disabled='false']) {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
